import type { AuthenticatedUser, MembershipSummary } from '@upweo/types';
import { createContext, useContext } from 'react';

/**
 * The context object and its hook, kept apart from the provider component.
 *
 * React Fast Refresh can only preserve state across edits when a module
 * exports nothing but components. Mixing the provider and this hook in one
 * file forces every consumer to remount on each keystroke in the provider —
 * which, for an auth provider, means being logged out mid-edit.
 */
export interface AuthState {
  readonly user: AuthenticatedUser | null;
  readonly activeStore: MembershipSummary | null;
  /** True until the initial session restore settles — prevents a login flash. */
  readonly isLoading: boolean;
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly selectStore: (tenantId: string) => Promise<void>;
  /**
   * Re-read the session from the server. Used after signup, where the API has
   * already issued a session and the SPA just needs to adopt it.
   */
  readonly refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
};
